if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        // Defer so children are guaranteed to be in the DOM when this element is
        // injected dynamically (e.g. via innerHTML in autocomplete results).
        Promise.resolve().then(() => this._init());
      }

      _init() {
        if (this._initialized) return;
        this._initialized = true;

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.form.querySelector('[name=id]').disabled = false;

        this.onSubmit = this.handleClickOnSubmit.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onOptionClick = this.handleOptionClick.bind(this);
        this.onCloseButtonClick = this.closeVariantSelector.bind(this);
        this.onVariantChanged = this.onSubmitHandler.bind(this);

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.form.addEventListener('submit', this.onSubmit);

        if (window.requestIdleCallback) {
          this.idleInitId = window.requestIdleCallback(() => this.initElements());
        } else {
          this.idleInitId = setTimeout(() => this.initElements(), 1);
        }
      }

      initElements() {
        this.productCardWrapper = this.closest('.js-product-card-wrapper');
        this.variantSelector = this.productCardWrapper?.querySelector('.variant-selector');
        // The card root is the reparent target; there is no separate js-card-element any more.
        this.cardElement = this.productCardWrapper;
        this.closeBtn = this.variantSelector?.querySelector('.selector__close');
        this.variantOptions = this.variantSelector?.querySelectorAll('.variant-selector__variant-cta');
        this.productFormVariantInput = this.productCardWrapper?.querySelector('.product-variant-id');
        this.bodyLayer = document.querySelector('.body-layer');

        // Contextual elements (PDP/sticky)
        const productContainer = this.closest('.product');
        this.variantRadios = productContainer?.querySelector('variant-radios');
        this.dropdownSelect = productContainer?.querySelector('dropdown-selects');
      }

      disconnectedCallback() {
        if (window.cancelIdleCallback && this.idleInitId) {
          window.cancelIdleCallback(this.idleInitId);
        } else if (this.idleInitId) {
          clearTimeout(this.idleInitId);
        }

        // On mobile the size sheet is moved to <body>, outside this card. If the card is
        // removed while the sheet is open (e.g. a filter/sort re-render landing late), the
        // sheet should be closed too
        if (this.variantSelector && !this.variantSelector.classList.contains('hidden')) {
          this.hideVariantSelector();
        }

        this.form?.removeEventListener('submit', this.onSubmit);
        this.variantRadios?.removeEventListener('variant:changed', this.onVariantChanged);
        this.cleanupVariantSelectorListeners();
      }

      handleClickOnSubmit(evt) {
        evt.preventDefault();

        // Refresh dynamic elements if not cached (safety for dynamic content)
        if (!this.variantRadios || !this.dropdownSelect) {
          const productContainer = this.closest('.product');
          this.variantRadios = this.variantRadios || productContainer?.querySelector('variant-radios');
          this.dropdownSelect = this.dropdownSelect || productContainer?.querySelector('dropdown-selects');
        }

        if (this.submitButton.dataset.hasVariants) {
          // Product card case: Open the size selector dropdown if the product has variants
          evt.stopImmediatePropagation();
          this.onOpenVariantSelector();
        } else if (this.variantRadios && !this.variantRadios.hasAttribute('selected')) {
          // PDP case when no size is selected
          evt.stopImmediatePropagation();
          if (this.closest('.appears-sticky') || this.closest('.floating-add-to-cart')) {
            // If in sticky buy bar -- show size selector
            if (typeof this.dropdownSelect?.openDropdown === 'function') {
              this.dropdownSelect.openDropdown();
            } else {
              // Fallback: element not upgraded yet -- click the real visible trigger.
              this.dropdownSelect?.querySelector('.js-dropdown-selected')?.click();
            }

            // Add listener to submit once variant is selected
            this.variantRadios.removeEventListener('variant:changed', this.onVariantChanged);
            this.variantRadios.addEventListener('variant:changed', this.onVariantChanged, { once: true });
          } else {
            // Show an error if no size is selected
            this.variantRadios.setAttribute('has-error', '');
          }
        } else {
          // otherwise proceed with form submission
          this.onSubmitHandler();
        }
      }

      onOpenVariantSelector() {
        // Remember where focus came from so it can be handed back on close.
        this.selectorReturnFocus = document.activeElement;

        this.productCardWrapper?.classList.add('active-form');
        this.productCardWrapper?.closest('.product-grid')?.classList.add('has-active-form');
        this.showVariantSelector();

        this.cleanupVariantSelectorListeners();

        document.addEventListener('click', this.onDocumentClick);
        this.closeBtn?.addEventListener('click', this.onCloseButtonClick);
        this.variantOptions?.forEach((option) => {
          option.addEventListener('click', this.onOptionClick);
        });
      }

      cleanupVariantSelectorListeners() {
        document.removeEventListener('click', this.onDocumentClick);
        this.closeBtn?.removeEventListener('click', this.onCloseButtonClick);
        this.variantOptions?.forEach((option) => {
          option.removeEventListener('click', this.onOptionClick);
        });
      }

      closeVariantSelector() {
        this.hideVariantSelector();
      }

      /** Where focus goes on close, falling back when the origin is gone or is
       * itself inside the panel about to be hidden. */
      get selectorReturnTarget() {
        const origin = this.selectorReturnFocus;
        const usable = origin?.isConnected && origin.offsetParent !== null && !this.variantSelector?.contains(origin);
        return usable ? origin : this.submitButton;
      }

      showVariantSelector() {
        if (!this.variantSelector) return;
        this.variantSelector.classList.remove('hidden');

        this.selectorOpenedAsSheet = window.innerWidth < 990;
        if (this.selectorOpenedAsSheet) {
          document.body.appendChild(this.variantSelector);
          document.body.classList.add('variant-selector-opening', 'overflow-hidden');
          if (this.bodyLayer) this.bodyLayer.classList.remove('hidden');
        }

        // Modality is set here, not in card-product.liquid, because it depends on the
        // breakpoint: as a sheet the panel covers the page, as a desktop popover it is
        // anchored inside its card and cannot be isolated without inerting its own
        // ancestors.
        this.variantSelector.getBoundingClientRect();
        FocusLayer.push(this.variantSelector, {
          modal: this.selectorOpenedAsSheet,
          contain: this.selectorOpenedAsSheet,
          lockScroll: false, // the sheet uses body.overflow-hidden below, not body.no-scroll
          trigger: this.selectorReturnFocus,
          initialFocus: this.variantSelector.querySelector('.variant-selector__variant-cta') || this.closeBtn,
          onDismiss: () => this.hideVariantSelector(),
        });
      }

      handleDocumentClick(event) {
        if (!this.variantSelector) return;
        if (this.variantSelector.contains(event.target)) return;

        // Ignore clicks inside UI layers stacked above this panel (size charts),
        if (event.closedLayer && !event.closedLayer.contains(this.variantSelector)) return;
        if (FocusLayer.isAboveLayerTarget(event.target, this.variantSelector)) return;
        if (event.target.closest?.('dropdown-selects-options')) return;

        this.hideVariantSelector();
      }

      handleOptionClick(event) {
        if (!this.productFormVariantInput) this.initElements();
        if (!this.productFormVariantInput) return;

        this.productFormVariantInput.value = event.currentTarget.dataset.variantId || event.target.dataset.variantId;

        // Hide first: onSubmitHandler hands the cart layer document.activeElement to
        // return focus to, and that is a CTA inside this panel, which by then would be
        // display:none. Nothing in onSubmitHandler reads the panel.
        this.hideVariantSelector();
        this.onSubmitHandler();
      }

      hideVariantSelector() {
        if (!this.variantSelector) return;

        // Pop before hiding: FocusLayer hands focus back only if the layer still
        // holds it, and it cannot see that on a hidden panel.
        FocusLayer.pop(this.variantSelector, { returnFocus: this.selectorReturnTarget });

        this.variantSelector.classList.add('hidden');
        this.cleanupVariantSelectorListeners();
        this.productCardWrapper?.classList.remove('active-form');
        this.productCardWrapper?.closest('.product-grid')?.classList.remove('has-active-form');

        if (this.selectorOpenedAsSheet) {
          if (this.cardElement) this.cardElement.appendChild(this.variantSelector);
          document.body.classList.remove('variant-selector-opening', 'overflow-hidden');
          if (this.bodyLayer) {
            this.bodyLayer.classList.add('hidden');
          }
          this.selectorOpenedAsSheet = false;
        }
      }

      async onSubmitHandler() {
        // aria-disabled covers genuinely unavailable (sold out); the loading class
        // covers a request already in flight.
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.submitButton.classList.contains('loading')) return;

        this.handleErrorMessage();

        // Disabled so a second click cannot add the same product again, and busy so it
        // is announced as working rather than unavailable. aria-disabled rather than
        // the disabled attribute keeps the button focusable, so a keyboard user does
        // not lose their place; the guard above rejects the activation.
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.section || section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const data = await response.json();

          if (data.status) {
            this.error = true;
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: data.errors || data.description,
              message: data.message,
            });
            this.handleErrorMessage(data.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }
          // reset error flag after successful add to cart
          this.error = false;

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: data,
            element: this,
          });

          const isCartReco = Boolean(this.closest('.js-cart-reco'));
          this.cart.renderContents(data, isCartReco);
          this.showAddedNotification(data);
          if (isCartReco) this.hideRecoItem();
        } catch (e) {
          this.error = true;
          console.error(e);
        } finally {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-busy');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage || soldOutMessage.classList.contains('hidden')) {
            this.submitButton.removeAttribute('aria-disabled');
          }

          if (!this.error) {
            // The button goes back to its normal label; the confirmation lives in the
            // card's variants row and stays for the rest of the page view.
            const card = this.productCardWrapper || this.closest('.js-product-card-wrapper');
            card?.classList.add('product-card--just-added');
          }
        }
      }

      /**
       * Show an "added to cart" success notification in the cart.
       * Must be called after the cart has re-rendered, since a full drawer
       * render replaces the notification container (and the templates in it).
       * @param {object} data - The /cart/add.js response (line item JSON)
       */
      showAddedNotification(data) {
        const title = data?.product_title || data?.items?.[0]?.product_title;
        if (!title || typeof window.showCartNotification !== 'function') return;

        window.showCartNotification('item-added', { title });
      }

      hideRecoItem() {
        const slideElement = this.closest('li') || this.closest('.slider-slide');
        if (slideElement) {
          // Hide visually and flag as added
          slideElement.classList.add('hidden');
          slideElement.dataset.addedToCart = 'true';

          // Move the card out of the track so it stops taking a slot, but keep it
          // in the tree so tracking context (e.g. closest('exponea-reco')) still
          // resolves. Removing a card changes what there is to scroll, so the
          // carousel has to re-measure.
          const carousel = slideElement.closest('.product-carousel');
          if (carousel) {
            carousel.appendChild(slideElement);
            carousel.refresh?.();
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
